import { useLazyQuery } from '@apollo/client';
import noop from 'lodash/noop';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { VALIDATE_WORKFLOW_URL } from 'api/WorkflowsAPI';
import { validateMultiContent } from 'api/queries';
import {
  getEdges,
  getIsTemplateValidationEnabled,
  getIsYesNoSplitDataToggleEnabled,
  getNodes,
  getWorkflow,
  updateIsAsyncLoading,
} from 'modules/workflows/ducks';
import { getNodesWithErrors, parseWorkflowForSaving } from 'modules/workflows/utils';
import { ApiMethod } from 'services/api';
import { useRestMutation } from 'utils/hooks/api/useRestMutation';
import { ErrorResponse, NodeError, ValidationResponse } from './useValidateWorkflow.types';
import {
  flattenResponseErrors,
  getTemplateIdMap,
  parseFrontendValidationResponse,
  parseTemplateValidationResponse,
} from './useValidateWorkflow.utils';

/**
 * This hook validates any templates and workflow separately.
 * Any invalid template should not prevent publish but should
 * be highlighted on the canvas
 *
 * There will be follow up work to update the validateWorkflow
 * endpoint in WF-5019
 */
export const useValidateWorkflow = ({
  onValidationSuccess = noop,
  onValidationError = noop,
}: {
  onValidationSuccess?: () => void;
  onValidationError: (data: NodeError[]) => void;
}) => {
  const dispatch = useDispatch();
  const workflow = useSelector(getWorkflow);
  const nodes = useSelector(getNodes);
  const edges = useSelector(getEdges);
  const [hasError, setHasError] = useState(false);
  const isTemplateValidationEnabled = useSelector(getIsTemplateValidationEnabled);
  const isYesNoSplitDataToggleEnabled = useSelector(getIsYesNoSplitDataToggleEnabled);

  // Map of template ids to their associated node ids
  const templateIdMap = useMemo(() => getTemplateIdMap(nodes), [nodes]);

  // Handles the response from the validation queries
  const handleValidation = useCallback(
    (resp: ValidationResponse[] = []) => {
      setHasError(false);
      const errors = flattenResponseErrors(resp);

      if (errors.length) {
        onValidationError(errors);
      } else {
        onValidationSuccess();
      }
    },
    [onValidationSuccess, onValidationError],
  );

  // Query to determine if the workflow is valid
  const validateWorkflowMutation = useRestMutation(VALIDATE_WORKFLOW_URL, ApiMethod.post);

  // Runs the query to validate the workflow
  const validateWorkflow = useCallback(async () => {
    try {
      const validationResult = await validateWorkflowMutation.mutateAsync({
        data: parseWorkflowForSaving({ ...workflow, nodes, edges }),
      });
      return validationResult?.data;
    } catch (error) {
      return { errors: (error as ErrorResponse)?.response?.data?.details || [] };
    }
  }, [workflow, nodes, edges]);

  // Check for any issues we identify on the frontend but *not* on the backend.
  // EG: yes/no split query properties which do not match the start trigger
  const validateFrontend = useCallback(() => {
    return Promise.resolve(parseFrontendValidationResponse(getNodesWithErrors(nodes)));
  }, [nodes]);

  // Query to determine if the templates associated with the campaigns are valid
  const [validateTemplates, { loading: isValidatingTemplates }] = useLazyQuery(
    validateMultiContent,
    { fetchPolicy: 'cache-and-network' },
  );

  // Runs all validation queries
  const validateAll = useCallback(() => {
    // Show the spinner
    dispatch(updateIsAsyncLoading(true));

    const templateIds = Array.from(templateIdMap.keys());
    const promises = [validateWorkflow()];

    // Only validate frontend if `YesNoSplitDataToggle` is enabled
    if (isYesNoSplitDataToggleEnabled) {
      promises.push(validateFrontend());
    }

    // Only run the template validation query if there are templates and the
    // flag is enabled
    if (templateIds.length && isTemplateValidationEnabled) {
      promises.push(
        validateTemplates({ variables: { templateIds } }).then((res) =>
          parseTemplateValidationResponse(res, templateIdMap),
        ),
      );
    }

    // Run all the queries
    Promise.all(promises)
      .then(handleValidation)
      .catch((e) => {
        setHasError(true);
        onValidationError(e);
      })
      .finally(() => dispatch(updateIsAsyncLoading(false)));
  }, [workflow, nodes, edges, isTemplateValidationEnabled]);

  return {
    validateWorkflow: validateAll,
    hasError,
    isLoading: isValidatingTemplates || validateWorkflowMutation.isLoading,
  };
};
